import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, CheckCircle } from 'lucide-react';

interface Props {
  prefillMc?: string;
  prefillDot?: string;
  defaultEntity?: 'carrier' | 'broker' | 'both';
  onSubmitted: () => void;
}

export const ManualFmcsaForm: React.FC<Props> = ({
  prefillMc,
  prefillDot,
  defaultEntity = 'carrier',
  onSubmitted,
}) => {
  const { refreshProfile } = useAuth();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [legalName, setLegalName] = useState('');
  const [dbaName, setDbaName] = useState('');
  const [mc, setMc] = useState(prefillMc ?? '');
  const [dot, setDot] = useState(prefillDot ?? '');
  const [entity, setEntity] = useState<'carrier' | 'broker' | 'both'>(defaultEntity);
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateAbbr, setStateAbbr] = useState('');
  const [zip, setZip] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!legalName.trim() || (!dot.trim() && !mc.trim()) || !phone.trim() || !street.trim() || !city.trim() || !stateAbbr.trim() || !zip.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('submit-manual-fmcsa', {
        body: {
          legal_name: legalName.trim(),
          dba_name: dbaName.trim() || null,
          mc_number: mc.replace(/\D/g, '') || null,
          dot_number: dot.replace(/\D/g, '') || null,
          entity_type: entity,
          phone: phone.trim(),
          street: street.trim(),
          city: city.trim(),
          state: stateAbbr.trim().toUpperCase(),
          zip: zip.trim(),
        },
      });
      if (fnErr || (data as any)?.error) {
        setError((data as any)?.error || fnErr?.message || 'Submission failed. Please try again.');
        return;
      }
      setSubmitted(true);
      toast({
        title: 'Submitted for review',
        description: "We'll verify your authority within 24 hours.",
      });
      await refreshProfile();
    } catch (e: any) {
      setError(e?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-[hsl(var(--brand-teal))]">
          <CheckCircle className="h-5 w-5" />
          <p className="font-semibold">Submitted for manual review</p>
        </div>
        <p className="text-sm text-muted-foreground">
          Thanks — we've received your authority details and will verify within 24 hours. You can continue
          to your dashboard now and use the platform as normal while we review.
        </p>
        <Button
          onClick={onSubmitted}
          className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg"
        >
          Continue to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-xs">
          FMCSA verification service is temporarily unavailable. Please enter your authority details
          manually — we'll review within 24 hours and unlock your account.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 gap-3">
        <Field label="Legal Company Name *" value={legalName} onChange={setLegalName} placeholder="Acme Logistics, LLC" />
        <Field label="DBA Name (optional)" value={dbaName} onChange={setDbaName} placeholder="Acme Freight" />

        <div className="grid grid-cols-2 gap-3">
          <Field label="MC Number" value={mc} onChange={(v) => setMc(v.replace(/\D/g, ''))} placeholder="123456" />
          <Field label="DOT Number (optional if you have an MC)" value={dot} onChange={(v) => setDot(v.replace(/\D/g, ''))} placeholder="686931" />
        </div>

        <div>
          <Label className="text-muted-foreground text-xs mb-2 block">Entity Type *</Label>
          <Select value={entity} onValueChange={(v) => setEntity(v as any)}>
            <SelectTrigger className="border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="carrier">Carrier</SelectItem>
              <SelectItem value="broker">Broker</SelectItem>
              <SelectItem value="both">Both (Carrier + Broker)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Field label="Phone *" value={phone} onChange={setPhone} placeholder="(555) 123-4567" />
        <Field label="Physical Street Address *" value={street} onChange={setStreet} placeholder="123 Main St" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="City *" value={city} onChange={setCity} placeholder="Memphis" />
          <Field label="State *" value={stateAbbr} onChange={(v) => setStateAbbr(v.toUpperCase().slice(0, 2))} placeholder="TN" />
          <Field label="ZIP *" value={zip} onChange={setZip} placeholder="38103" />
        </div>
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : 'Submit for Manual Review'}
      </Button>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <div>
    <Label className="text-muted-foreground text-xs mb-2 block">{label}</Label>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-4"
    />
  </div>
);

export default ManualFmcsaForm;
